import { normalizeWidgetPreferences } from "@/containers/Dashboard/helpers/normalizeWidgetPreferences";
import {
  DASHBOARD_WIDGET_PREFERENCES_VERSION,
  DASHBOARD_WIDGETS,
  getDefaultWidgetPreferences,
} from "@/containers/Dashboard/widgets";
import { useLocalStorage } from "@/helpers";
import { useUserContext } from "@/providers/UserProvider";
import { useConfigurationQuery } from "@/services/appConfig";
import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

export const getDashboardWidgetStorageKey = (userId) =>
  `viper:dashboardWidgets:${userId || "anonymous"}`;

export const useDashboardWidgetPreferences = () => {
  const intl = useIntl();
  const { data: configuration, isError } = useConfigurationQuery(
    {},
    { pollingInterval: 30000, refetchOnMountOrArgChange: true },
  );
  const keyMilestonesEnabled =
    !isError && configuration?.keyMilestoneDashboardEnabled === true;

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isError]);

  const { userToken } = useUserContext();
  const userId = userToken?.user?.id;
  const storageKey = getDashboardWidgetStorageKey(userId);

  const [storedPreferences, setStoredPreferences] = useLocalStorage(
    storageKey,
    null,
  );

  const widgets = useMemo(() => {
    const normalized = normalizeWidgetPreferences(
      storedPreferences,
      DASHBOARD_WIDGETS,
    );

    return normalized.map((preference) => {
      const registryEntry = DASHBOARD_WIDGETS.find(
        ({ id }) => id === preference.id,
      );

      return {
        ...preference,
        titleId: registryEntry.titleId,
        Component: registryEntry.Component,
      };
    });
  }, [storedPreferences]);

  const availableWidgets = useMemo(
    () =>
      widgets.filter(
        ({ id }) => id !== "keyMilestones" || keyMilestonesEnabled,
      ),
    [widgets, keyMilestonesEnabled],
  );

  const visibleWidgets = useMemo(
    () => availableWidgets.filter(({ visible }) => visible),
    [availableWidgets],
  );

  const persist = (nextWidgets) => {
    setStoredPreferences({
      version: DASHBOARD_WIDGET_PREFERENCES_VERSION,
      widgets: nextWidgets.map(({ id, visible }) => ({ id, visible })),
    });
  };

  const setOrder = (orderedItems) => {
    const widgetsById = new Map(widgets.map((widget) => [widget.id, widget]));
    const orderedIds = new Set(orderedItems.map(({ id }) => id));
    let index = 0;

    // Keep unavailable widgets and their preferences in their saved slots.
    persist(
      widgets.map((widget) =>
        orderedIds.has(widget.id)
          ? widgetsById.get(orderedItems[index++].id)
          : widget,
      ),
    );
  };

  const setVisible = (id, visible) => {
    persist(
      widgets.map((widget) =>
        widget.id === id ? { id: widget.id, visible } : widget,
      ),
    );
  };

  const reset = () => {
    setStoredPreferences(getDefaultWidgetPreferences());
  };

  return {
    storageKey,
    widgets: availableWidgets,
    visibleWidgets,
    setOrder,
    setVisible,
    reset,
  };
};
